export type AttendanceClockAction = 'in' | 'out';

export interface AttendanceClockRow {
  user_id?: string | number | null;
  employee_id?: string | number | null;
  clock_in_time?: string | null;
  clock_out_time?: string | null;
  out_time?: string | null;
}

export interface AttendanceClockState {
  active: boolean;
  doneForToday: boolean;
  hasClockedIn: boolean;
  hasClockedOut: boolean;
  nextAction: AttendanceClockAction | null;
  onLeave: boolean;
}

const hasTime = (value: unknown): boolean =>
  typeof value === 'string' && value !== '' && value !== '00:00:00';

export const localToday = (date = new Date()): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export const localDayOffset = (days: number, date = new Date()): string => {
  const shifted = new Date(date);
  shifted.setDate(shifted.getDate() + days);
  return localToday(shifted);
};

export const getAttendanceUserId = (user: any): string | null => {
  const id = user?.user_id ?? user?.id ?? user?.sub ?? user?.employee_id ?? null;
  return id === null || id === undefined || id === '' ? null : String(id);
};

export const getAttendanceUserIds = (user: any): string[] =>
  Array.from(
    new Set(
      [user?.user_id, user?.id, user?.sub, user?.employee_id]
        .filter((id) => id !== null && id !== undefined && id !== '')
        .map(String),
    ),
  );

/** True when `now` is past "HH:MM" on the same local day. No bound → never. */
export const isPastShiftEnd = (
  officeEnd: string | null | undefined,
  now: number = Date.now(),
): boolean => {
  if (!officeEnd) return false;
  const [hh, mm] = String(officeEnd).split(':').map(Number);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return false;
  const end = new Date(now);
  end.setHours(hh, mm, 0, 0);
  return now > end.getTime();
};

export const deriveAttendanceClockState = (
  rows: AttendanceClockRow[],
  userId: string | string[] | null,
  sessionClock: boolean,
  carryoverRows: AttendanceClockRow[] = [],
  // Now is past the employee's office end time. Only relevant when
  // sessionClock is false — remote/overtime/elevated have no shift bound.
  shiftEnded = false,
): AttendanceClockState => {
  const acceptedUserIds = new Set(
    (Array.isArray(userId) ? userId : userId ? [userId] : []).map(String),
  );
  const rowsForUser = (source: AttendanceClockRow[]) =>
    acceptedUserIds.size > 0
      ? source.filter((row) => {
          const rowUserId = row.user_id ?? row.employee_id;
          return (
            rowUserId !== null &&
            rowUserId !== undefined &&
            acceptedUserIds.has(String(rowUserId))
          );
        })
      : [];
  const mine = rowsForUser(rows);
  const activeWindow = [...rowsForUser(carryoverRows), ...mine];

  const inCount = mine.filter((row) => hasTime(row.clock_in_time)).length;
  const outCount = mine.filter((row) => hasTime(row.clock_out_time ?? row.out_time)).length;
  const activeInCount = activeWindow.filter((row) => hasTime(row.clock_in_time)).length;
  const activeOutCount = activeWindow.filter((row) => hasTime(row.clock_out_time ?? row.out_time)).length;
  const hasClockedIn = inCount > 0;
  const hasClockedOut = outCount > 0;
  const active = activeInCount > activeOutCount;

  if (sessionClock) {
    return {
      active,
      doneForToday: false,
      hasClockedIn,
      hasClockedOut,
      nextAction: active ? 'out' : 'in',
      onLeave: false,
    };
  }

  // "Currently clocked in" is a punch-count comparison, not "has any open row".
  // A row now holds a clock-in and its subsequent clock-out (a closed session =
  // one row with BOTH times; an open session = one row with only a clock-in).
  // Counting rows-with-in-time vs rows-with-out-time still works: a closed row
  // adds +1 to each, an open row adds +1 to INs only. So a multi-punch day
  // (e.g. a lunch break) that ends on an open clock-in has more INs than OUTs
  // and is correctly "still clocked in" — using `hasClockedOut` here would
  // wrongly mark it "done" and disable the sidebar Clock Out.
  //
  // In-office attendance is NOT one punch a day: people step out and come back,
  // so they clock in and out in PAIRS all through office hours. What closes the
  // day is the SHIFT END, not "has clocked in at all" — and even then only for
  // STARTING a punch. An open punch can always be closed, otherwise someone who
  // works till 18:15 on an 18:00 shift is stuck clocked in.
  const doneForToday = !active && shiftEnded;
  return {
    active,
    doneForToday,
    hasClockedIn,
    hasClockedOut,
    nextAction: active ? 'out' : doneForToday ? null : 'in',
    onLeave: false,
  };
};
